import {
  Box,
  Button,
  Flex,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';
import { EditableIncome } from '../hooks/useIncomes';
import { incomeTypeOptions } from '../incomeTypes';
import { conversionPreviewNote } from '../leftOut';
import { moneyOrNotRecorded } from '../notRecorded';
import { calculateAnnualAmount, calculateMonthlyAmount, cashflowFrequencyOptions } from '../utils';
import { optionalAmountValidator, recordedAmount } from './amountFields';
import { currencySymbol } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';

type IncomeModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (income: EditableIncome) => Promise<void>;
  initialValues?: EditableIncome;
};

const defaultValues: EditableIncome = {
  clientId: '',
  id: undefined,
  description: '',
  incomeType: 'Employment',
  frequency: 'Monthly',
  amountPreTax: null,
  amountPostTax: null,
};

export function IncomeModal({ opened, onClose, onSubmit, initialValues }: IncomeModalProps) {
  const currency = useCurrency();
  const isEditing = !!initialValues?.id;

  const form = useForm<EditableIncome>({
    initialValues: initialValues || defaultValues,
    validate: {
      frequency: value => (value ? null : 'Choose a frequency'),
      amountPreTax: optionalAmountValidator('Amount before tax'),
      amountPostTax: optionalAmountValidator('Amount after tax'),
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (opened) {
      form.setValues(initialValues || defaultValues);
      // Detected records can arrive with required fields missing, so flag them on open
      if (initialValues) {
        form.validate();
      }
    }
  }, [opened, initialValues]);

  const handleSubmit = async (values: EditableIncome) => {
    try {
      await onSubmit({
        ...values,
        amountPreTax: recordedAmount(values.amountPreTax),
        amountPostTax: recordedAmount(values.amountPostTax),
      });
    } catch {
      // Already reported by the caller; keep what was typed so it can be saved again.
    }
  };

  const monthlyPreview = (amount: number | string | null) =>
    moneyOrNotRecorded(
      calculateMonthlyAmount(recordedAmount(amount), form.values.frequency),
      currency,
    );

  const annualPreview = (amount: number | string | null) =>
    moneyOrNotRecorded(
      calculateAnnualAmount(recordedAmount(amount), form.values.frequency),
      currency,
    );

  const frequency = form.values.frequency;
  const amountLabelSuffix = frequency ? ` (${frequency.toLowerCase()})` : '';
  const previewNote = conversionPreviewNote(frequency);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Edit income' : 'Add income'}
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)} data-testid="income-modal">
        <Stack gap="xs">
          <Select
            label="Type"
            labelProps={{ variant: 'light' }}
            data={incomeTypeOptions}
            {...form.getInputProps('incomeType')}
            required
            allowDeselect={false}
          />
          <TextInput
            label="Description"
            labelProps={{ variant: 'light' }}
            {...form.getInputProps('description')}
          />
          <Select
            label="Frequency"
            labelProps={{ variant: 'light' }}
            data={cashflowFrequencyOptions}
            {...form.getInputProps('frequency')}
            required
            allowDeselect={false}
          />
          <Group grow>
            <NumberInput
              label={`Amount before tax${amountLabelSuffix}`}
              labelProps={{ variant: 'light' }}
              {...form.getInputProps('amountPreTax')}
              value={form.values.amountPreTax ?? ''}
              leftSection={currencySymbol(currency)}
              hideControls
            />
            <NumberInput
              label={`Amount after tax${amountLabelSuffix}`}
              labelProps={{ variant: 'light' }}
              {...form.getInputProps('amountPostTax')}
              value={form.values.amountPostTax ?? ''}
              leftSection={currencySymbol(currency)}
              hideControls
            />
          </Group>
        </Stack>

        <Box mt="xl">
          <Flex align="center" gap="md" mb="sm">
            <Text fw={500} size="xs" c="gray.4" p={0} m={0}>
              Income preview
            </Text>
            <Box flex={1} h={1} bg="gray.2" />
          </Flex>
          {previewNote === null ? (
            <Stack gap="sm">
              <Stack gap="xxxs">
                <Flex justify="space-between">
                  <Text fw={600} size="md" c="gray.10">
                    Before tax{' '}
                    <Text span c="gray.4">
                      (monthly)
                    </Text>
                  </Text>
                  <Text fw={600} size="md" c="gray.10">
                    {monthlyPreview(form.values.amountPreTax)}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text fw={600} size="md" c="gray.10">
                    After tax{' '}
                    <Text span c="gray.4">
                      (monthly)
                    </Text>
                  </Text>
                  <Text fw={600} size="md" c="gray.10">
                    {monthlyPreview(form.values.amountPostTax)}
                  </Text>
                </Flex>
              </Stack>
              <Stack gap="xxxs">
                <Flex justify="space-between">
                  <Text fw={600} size="md" c="gray.10">
                    Before tax{' '}
                    <Text span c="gray.4">
                      (annually)
                    </Text>
                  </Text>
                  <Text fw={600} size="md" c="gray.10">
                    {annualPreview(form.values.amountPreTax)}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  <Text fw={600} size="md" c="gray.10">
                    After tax{' '}
                    <Text span c="gray.4">
                      (annually)
                    </Text>
                  </Text>
                  <Text fw={600} size="md" c="gray.10">
                    {annualPreview(form.values.amountPostTax)}
                  </Text>
                </Flex>
              </Stack>
            </Stack>
          ) : (
            <Text size="sm" c="gray.6">
              {previewNote}
            </Text>
          )}
        </Box>

        <Group justify="flex-end" mt="xl">
          <Button variant="filled-white" fullWidth type="submit">
            {isEditing ? 'Confirm & save' : 'Confirm & add'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
